/**
 * CalendarUnifiedPage — G2 + G2-UX.1: Calendário Unificado (peça central do Gestor).
 *
 * Visão de produto (18/08/2026):
 *   Hoje = operação do dia · Calendário = tudo planejado no tempo ·
 *   Relatórios = avaliação do que aconteceu. Agenda não existe para o Gestor.
 *
 * G2-UX.1 (rodada visual):
 *   • Preto como cor estrutural (CTA, tab ativa); cor só quando tem significado
 *     (fases pintam os dias do grid; badges de área são discretos).
 *   • Header padrão do sistema (Layout) — sem header exclusivo com logo.
 *   • Card único de controles: views + Temporada/Escopo/Área.
 *   • "Em vigor no período" agrupado por área (conta + expande), nunca lista crua.
 *   • Grid mensal navegável (◀ ▶), dias pintados pela cor da fase vigente.
 *   • Squads via /gestor/calendar/squads (tenant derivado — fix do 400).
 *
 * Fonte única: GET /api/gestor/calendar/events (read-only). O Gestor edita
 * SOMENTE temporadas/fases; Coach/Prep/Nutri mantêm suas áreas.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AZURE } from '../../services/api';
import {
  getSeasons, getUnifiedEvents, PHASE_LABELS, PHASE_COLORS,
} from '../../services/gestorCalendarService';

const INK = '#111827';       // preto estrutural
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const BG = '#f8fafc';

const VIEWS = [
  { id: 'today', label: 'Hoje' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês' },
  { id: 'season', label: 'Temporada' },
];

const AREA_OPTIONS = [
  { id: 'all', label: 'Todas' },
  { id: 'training', label: 'Técnico' },
  { id: 'physical', label: 'Físico' },
  { id: 'nutrition', label: 'Nutrição' },
  { id: 'competition', label: 'Competição' },
];

// Badges discretos: ponto colorido + texto — sem backgrounds saturados.
const AREA_META = {
  training: { label: 'Técnico', dot: '#111827' },
  physical: { label: 'Físico', dot: '#b45309' },
  nutrition: { label: 'Nutrição', dot: '#15803d' },
  competition: { label: 'Competição', dot: '#b91c1c' },
};

const SCOPE_OPTIONS = [
  { id: 'all', label: 'Todos' },
  { id: 'category', label: 'Categoria' },
  { id: 'squad', label: 'Squad' },
  { id: 'athlete', label: 'Atleta' },
];

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WEEKDAYS = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'];

const iso = (d) => d.toISOString().slice(0, 10);
const fmt = (s) => (s ? s.slice(0, 10).split('-').reverse().join('/') : '—');

function windowFor(view, season, monthRef) {
  const today = new Date();
  if (view === 'today') return { from: iso(today), to: iso(today) };
  if (view === 'week') {
    const d = new Date(today);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // segunda
    const end = new Date(d); end.setDate(d.getDate() + 6);
    return { from: iso(d), to: iso(end) };
  }
  if (view === 'month') {
    const from = new Date(monthRef.getFullYear(), monthRef.getMonth(), 1);
    const to = new Date(monthRef.getFullYear(), monthRef.getMonth() + 1, 0);
    return { from: iso(from), to: iso(to) };
  }
  if (season) return { from: season.start_date.slice(0, 10), to: season.end_date.slice(0, 10) };
  const to = new Date(today); to.setMonth(to.getMonth() + 6);
  return { from: iso(today), to: iso(to) };
}

/** Fase vigente numa data — precedência GROUP > CATEGORY > CLUB. */
function phaseOn(phases, dayIso) {
  const rank = { GROUP: 3, CATEGORY: 2, CLUB: 1, TEAM: 1 };
  let best = null;
  for (const p of phases) {
    const s = p.start_date.slice(0, 10);
    const e = p.end_date.slice(0, 10);
    if (dayIso >= s && dayIso <= e) {
      if (!best || (rank[p.scope] || 0) > (rank[best.scope] || 0)) best = p;
    }
  }
  return best;
}

const AreaDot = ({ area }) => {
  const m = AREA_META[area] || AREA_META.training;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: MUTED }}>
      <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: m.dot }} />
      {m.label}
    </span>
  );
};

/** Grid mensal navegável — dias pintados (barra fina) pela fase vigente. */
const MonthGrid = ({ monthRef, setMonthRef, phases, dayCounts, onDayClick }) => {
  const first = new Date(monthRef.getFullYear(), monthRef.getMonth(), 1);
  const daysInMonth = new Date(monthRef.getFullYear(), monthRef.getMonth() + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // segunda = 0
  const todayIso = iso(new Date());
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(iso(new Date(monthRef.getFullYear(), monthRef.getMonth(), d)));
  }
  const nav = (delta) => setMonthRef(new Date(monthRef.getFullYear(), monthRef.getMonth() + delta, 1));

  return (
    <div className="bg-white rounded-xl border p-4" style={{ borderColor: BORDER }}>
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => nav(-1)} aria-label="Mês anterior"
          className="w-8 h-8 rounded-lg border flex items-center justify-center text-gray-600 hover:bg-gray-50"
          style={{ borderColor: BORDER }}>‹</button>
        <h3 className="text-sm font-black uppercase tracking-wide" style={{ color: INK }}>
          {MONTHS[monthRef.getMonth()]} {monthRef.getFullYear()}
        </h3>
        <button onClick={() => nav(1)} aria-label="Próximo mês"
          className="w-8 h-8 rounded-lg border flex items-center justify-center text-gray-600 hover:bg-gray-50"
          style={{ borderColor: BORDER }}>›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[10px] font-bold" style={{ color: MUTED }}>{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((dayIso, i) => {
          if (!dayIso) return <div key={`x${i}`} />;
          const ph = phaseOn(phases, dayIso);
          const count = dayCounts[dayIso] || 0;
          const isToday = dayIso === todayIso;
          return (
            <button key={dayIso} onClick={() => onDayClick(dayIso)}
              className="relative rounded-lg border text-left p-1.5 h-14 hover:bg-gray-50 transition-colors"
              style={{ borderColor: isToday ? INK : BORDER, borderWidth: isToday ? 2 : 1 }}
              title={ph ? `${PHASE_LABELS[ph.block_type] || ph.block_type} · ${ph.block_name}` : undefined}>
              <span className="text-xs font-bold" style={{ color: isToday ? INK : '#374151' }}>
                {Number(dayIso.slice(8, 10))}
              </span>
              {count > 0 && (
                <span className="absolute top-1 right-1.5 text-[10px] font-bold" style={{ color: MUTED }}>
                  {count}
                </span>
              )}
              {ph && (
                <span className="absolute bottom-1 left-1.5 right-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: PHASE_COLORS[ph.block_type] || INK }} />
              )}
            </button>
          );
        })}
      </div>
      {/* Legenda das fases visíveis no mês */}
      {phases.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t" style={{ borderColor: BORDER }}>
          {[...new Set(phases.map((p) => p.block_type))].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 text-xs" style={{ color: MUTED }}>
              <span className="w-3 h-1.5 rounded-full inline-block"
                style={{ backgroundColor: PHASE_COLORS[t] || INK }} />
              {PHASE_LABELS[t] || t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const CalendarUnifiedPage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('month');
  const [monthRef, setMonthRef] = useState(() => {
    const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [scope, setScope] = useState('all');
  const [scopeRef, setScopeRef] = useState('');
  const [area, setArea] = useState('all');
  const [categories, setCategories] = useState([]);
  const [squads, setSquads] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [events, setEvents] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedArea, setExpandedArea] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Dimensões de filtro — todas tenant-derived no backend (fix /squads 400).
  useEffect(() => {
    getSeasons().then((s) => { setSeasons(s); if (s[0]) setSeasonId(s[0].season_id); }).catch(() => {});
    AZURE.get('/gestor/calendar/categories')
      .then(({ data }) => setCategories(data.data || [])).catch(() => setCategories([]));
    AZURE.get('/gestor/calendar/squads')
      .then(({ data }) => setSquads(data.data || [])).catch(() => setSquads([]));
    AZURE.get('/gestor/calendar/athletes')
      .then(({ data }) => setAthletes(data.data || [])).catch(() => setAthletes([]));
  }, []);

  const season = useMemo(() => seasons.find((s) => s.season_id === seasonId) || null, [seasons, seasonId]);
  const win = useMemo(() => windowFor(view, season, monthRef), [view, season, monthRef]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { dateFrom: win.from, dateTo: win.to, area };
      if (scope === 'category' && scopeRef) params.category = scopeRef;
      if (scope === 'squad' && scopeRef) params.squadId = scopeRef;
      if (scope === 'athlete' && scopeRef) params.athleteUserId = scopeRef;
      const data = await getUnifiedEvents(params);
      setEvents(data.events || []);
      setPhases(data.phases || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao carregar calendário');
    } finally { setLoading(false); }
  }, [win.from, win.to, area, scope, scopeRef]);

  useEffect(() => { load(); setSelectedDay(null); }, [load]);

  const dated = useMemo(() => events.filter((e) => e.date), [events]);
  const ranges = useMemo(() => events.filter((e) => e.kind === 'range'), [events]);

  const dayCounts = useMemo(() => {
    const m = {};
    dated.forEach((e) => {
      const k = e.date.slice(0, 10);
      m[k] = (m[k] || 0) + (e.planned || 1);
    });
    return m;
  }, [dated]);

  const byDay = useMemo(() => {
    const days = {};
    dated.forEach((e) => {
      const k = e.date.slice(0, 10);
      (days[k] = days[k] || []).push(e);
    });
    return Object.entries(days).sort(([a], [b]) => a.localeCompare(b));
  }, [dated]);

  // "Em vigor no período": agrupado por área — contexto, não agenda.
  const rangesByArea = useMemo(() => {
    const g = {};
    ranges.forEach((e) => { (g[e.area] = g[e.area] || []).push(e); });
    return g;
  }, [ranges]);

  // Fase atual (na data de hoje, dentro da janela)
  const currentPhase = useMemo(() => phaseOn(phases, iso(new Date())), [phases]);

  const openEvent = (ev) => {
    const t = ev.detail || {};
    if (t.type === 'training_plan') navigate(`/staff/training?plan=${t.id}`);
    else if (t.type === 'training_day') navigate(`/manager/training-overview?date=${t.id?.slice?.(0, 10) || t.id}`);
    else if (t.type === 'nutrition_plan') navigate('/manager/athlete-diets');
    else if (t.type === 'competition') navigate(`/natacao/campeonato/${t.id}`);
  };

  const select = 'px-3 py-2 border rounded-lg text-sm bg-white';
  const selStyle = { borderColor: BORDER, color: INK };
  const visibleDays = selectedDay ? byDay.filter(([d]) => d === selectedDay) : byDay;

  const DayList = ({ entries }) => (
    <div className="space-y-3">
      {entries.map(([day, evs]) => (
        <div key={day} className="bg-white rounded-xl border p-4" style={{ borderColor: BORDER }}>
          <h3 className="text-sm font-black mb-2 capitalize" style={{ color: INK }}>
            {new Date(day + 'T12:00:00').toLocaleDateString('pt-BR',
              { weekday: 'long', day: '2-digit', month: '2-digit' })}
          </h3>
          <div className="divide-y" style={{ borderColor: BORDER }}>
            {evs.map((ev, i) => (
              <button key={i} onClick={() => openEvent(ev)}
                className="w-full text-left py-2 flex items-center gap-3 hover:bg-gray-50 rounded px-1">
                <AreaDot area={ev.area} />
                <span className="text-sm font-semibold flex-1 truncate" style={{ color: INK }}>
                  {ev.title}{ev.time ? ` · ${ev.time}` : ''}
                </span>
                {ev.kind === 'day_summary' && (
                  <span className="text-xs" style={{ color: MUTED }}>
                    {ev.completed}/{ev.planned} · {ev.athletes} atletas
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        {/* Título da página — header/logo vêm do Layout padrão do sistema */}
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-black tracking-tight" style={{ color: INK }}>Calendário</h1>
          <Link to="/manager/calendar/manage"
            className="px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
            style={{ backgroundColor: INK }}>
            Gerenciar temporadas e fases
          </Link>
        </div>
        <p className="text-sm mb-5" style={{ color: MUTED }}>
          Planejamento, fases e atividades do clube
        </p>

        {/* Card único de controles: views + filtros */}
        <div className="bg-white rounded-xl border p-4 mb-4" style={{ borderColor: BORDER }}>
          <div className="flex gap-1 mb-3">
            {VIEWS.map((v) => (
              <button key={v.id} onClick={() => setView(v.id)}
                className="px-4 py-1.5 rounded-lg text-sm font-bold transition-colors"
                style={view === v.id
                  ? { backgroundColor: INK, color: '#fff' }
                  : { color: MUTED }}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: MUTED }}>Temporada</label>
              {seasons.length === 0 ? (
                <Link to="/manager/calendar/manage"
                  className="inline-block px-3 py-2 border rounded-lg text-sm font-semibold hover:bg-gray-50"
                  style={{ borderColor: BORDER, color: INK }}>
                  Nenhuma temporada ativa — criar
                </Link>
              ) : (
                <select className={select} style={selStyle} value={seasonId || ''}
                  onChange={(e) => setSeasonId(Number(e.target.value) || null)}>
                  {seasons.map((s) => <option key={s.season_id} value={s.season_id}>{s.label}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: MUTED }}>Escopo</label>
              <select className={select} style={selStyle} value={scope}
                onChange={(e) => { setScope(e.target.value); setScopeRef(''); }}>
                {SCOPE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            {scope === 'category' && (
              <select className={select} style={selStyle} value={scopeRef} onChange={(e) => setScopeRef(e.target.value)}>
                <option value="">Categoria…</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {scope === 'squad' && (
              <select className={select} style={selStyle} value={scopeRef} onChange={(e) => setScopeRef(e.target.value)}>
                <option value="">Squad…</option>
                {squads.map((s) => <option key={s.squad_id} value={s.squad_id}>{s.name}</option>)}
              </select>
            )}
            {scope === 'athlete' && (
              <select className={select} style={selStyle} value={scopeRef} onChange={(e) => setScopeRef(e.target.value)}>
                <option value="">Atleta…</option>
                {athletes.map((a) => (
                  <option key={a.athlete_user_id} value={a.athlete_user_id}>{a.athlete_name}</option>
                ))}
              </select>
            )}
            <div>
              <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: MUTED }}>Área</label>
              <select className={select} style={selStyle} value={area} onChange={(e) => setArea(e.target.value)}>
                {AREA_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <span className="text-xs ml-auto pb-2" style={{ color: MUTED }}>{fmt(win.from)} — {fmt(win.to)}</span>
          </div>
        </div>

        {/* Fase atual — informação, não decoração */}
        {currentPhase && (
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: MUTED }}>Fase atual</span>
            <span className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: INK }}>
              <span className="w-3 h-3 rounded-full inline-block"
                style={{ backgroundColor: PHASE_COLORS[currentPhase.block_type] || INK }} />
              {PHASE_LABELS[currentPhase.block_type] || currentPhase.block_type}
              <span className="font-medium" style={{ color: MUTED }}>
                · {fmt(currentPhase.start_date)} — {fmt(currentPhase.end_date)}
                · {currentPhase.scope === 'CLUB' || currentPhase.scope === 'TEAM'
                  ? 'Clube inteiro' : currentPhase.scope_ref}
              </span>
            </span>
          </div>
        )}

        {error && (
          <div className="bg-white border rounded-xl p-4 mb-4 text-sm"
            style={{ borderColor: '#fca5a5', color: '#b91c1c' }}>{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="bg-white h-24 rounded-xl animate-pulse border" style={{ borderColor: BORDER }} />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Coluna principal */}
            <div className="lg:col-span-2 space-y-4">
              {(view === 'month' || view === 'season') && (
                <MonthGrid monthRef={monthRef} setMonthRef={setMonthRef}
                  phases={phases} dayCounts={dayCounts}
                  onDayClick={(d) => setSelectedDay((cur) => (cur === d ? null : d))} />
              )}
              {selectedDay && visibleDays.length === 0 && (
                <div className="bg-white rounded-xl border p-6 text-center text-sm"
                  style={{ borderColor: BORDER, color: MUTED }}>
                  Nada planejado em {fmt(selectedDay)}.
                </div>
              )}
              {visibleDays.length > 0
                ? <DayList entries={visibleDays} />
                : !selectedDay && (
                  <div className="bg-white rounded-xl border p-10 text-center text-sm"
                    style={{ borderColor: BORDER, color: MUTED }}>
                    Nada planejado nesta janela.
                  </div>
                )}
            </div>

            {/* Coluna lateral: contexto agrupado */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border p-4" style={{ borderColor: BORDER }}>
                <h2 className="text-[10px] font-black uppercase tracking-widest mb-3" style={{ color: MUTED }}>
                  Em vigor no período
                </h2>
                {Object.keys(rangesByArea).length === 0 ? (
                  <p className="text-sm" style={{ color: MUTED }}>Nenhum plano ativo na janela.</p>
                ) : (
                  <div className="divide-y" style={{ borderColor: BORDER }}>
                    {Object.entries(rangesByArea).map(([a, list]) => (
                      <div key={a}>
                        <button onClick={() => setExpandedArea((cur) => (cur === a ? null : a))}
                          className="w-full flex items-center justify-between py-2.5 hover:bg-gray-50 rounded px-1">
                          <AreaDot area={a} />
                          <span className="text-xs font-semibold" style={{ color: INK }}>
                            {list.length} {a === 'nutrition' ? 'planos ativos' : 'itens'}
                            <span className="ml-1" style={{ color: MUTED }}>{expandedArea === a ? '▾' : '›'}</span>
                          </span>
                        </button>
                        {expandedArea === a && (
                          <div className="pb-2 pl-4 space-y-1">
                            {list.map((ev, i) => (
                              <button key={i} onClick={() => openEvent(ev)}
                                className="w-full text-left text-xs py-1 hover:underline truncate block"
                                style={{ color: INK }}>
                                {ev.title}
                                <span style={{ color: MUTED }}> · {fmt(ev.start_date)} — {fmt(ev.end_date)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarUnifiedPage;
